import { Paper, Text, Box, ActionIcon, Flex, Tooltip } from '@mantine/core';
import { IconX, IconArrowBackUp, IconMicrophone } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { CalendarEvent } from 'src/meetings/models';
import { EventTime } from './EventTime';
import { useDeleteCalendarEventMutation } from '../hooks/useDeleteCalendarEventMutation';
import { useRestoreCalendarEventMutation } from '../hooks/useRestoreCalendarEventMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useEffect, useState } from 'react';
import { useTimeout } from '@mantine/hooks';
import {
  formatClientLabel,
  getClientDisplayNames,
  getFirstClientParticipant,
} from 'src/meetings/utils/event-utils';
import { useClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';

type Props = {
  event: CalendarEvent;
  onClick?: () => void;
  isSelected?: boolean;
  onRemove: () => void;
};

export const UpcomingEventCard = ({ event, onClick, onRemove, isSelected }: Props) => {
  const { deleteCalendarEvent } = useDeleteCalendarEventMutation();
  const { restoreCalendarEvent } = useRestoreCalendarEventMutation();
  const clientFamilyMap = useClientFamilyMap();
  // The hub spans every family, so collapse each family's attendees to one
  // "<name> family" label rather than listing individual members.
  const clientLabel = formatClientLabel(getClientDisplayNames(event, clientFamilyMap));

  const [deletedAt, setDeletedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const isDeleting = deletedAt != null && now < deletedAt + 10000;
  const secondsRemaining =
    deletedAt != null ? Math.max(0, Math.ceil((deletedAt + 10000 - now) / 1000)) : 0;
  const { start: startDeleteTimer, clear: clearDeleteTimer } = useTimeout(onRemove, 10000);

  useEffect(() => {
    if (deletedAt == null) {
      clearDeleteTimer();
      return;
    }

    const interval = setInterval(() => {
      const currentTime = Date.now();
      setNow(currentTime);
      if (currentTime >= deletedAt + 10000) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [deletedAt, clearDeleteTimer]);

  const handleRemoveEvent = async (eventId: string) => {
    try {
      await deleteCalendarEvent(eventId);
      setDeletedAt(Date.now());
      startDeleteTimer();
    } catch (error) {
      setDeletedAt(null);
      notifyAndLogError(
        'Failed to remove event',
        'An error occurred while trying to remove the event. Please try again later.',
        error,
        { eventId },
      );
    }
  };

  const handleRestoreEvent = async (eventId: string) => {
    try {
      setDeletedAt(null);
      clearDeleteTimer();
      await restoreCalendarEvent(eventId);
    } catch (error) {
      notifyAndLogError(
        'Failed to restore event',
        'An error occurred while trying to restore the event. Please contact support.',
        error,
        { eventId },
      );
    }
  };

  return (
    <Paper
      variant="card"
      pos="relative"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        border: isSelected ? '2px solid #FF771C' : undefined,
      }}
    >
      {isDeleting ? (
        <Flex
          align="center"
          gap="8px"
          style={{
            position: 'absolute',
            top: '12px',
            right: '16px',
            cursor: 'pointer',
          }}
          onClick={e => {
            e.stopPropagation();
            handleRestoreEvent(event.id);
          }}
        >
          <Text size="sm" fw={FONT_WEIGHTS.semibold} c="#B2B2B2">
            Undo
          </Text>
          <ActionIcon variant="subtle" color="gray">
            <IconArrowBackUp size={20} />
          </ActionIcon>
        </Flex>
      ) : (
        <ActionIcon
          variant="subtle"
          color="gray"
          style={{
            position: 'absolute',
            top: '12px',
            right: '16px',
          }}
          onClick={e => {
            e.stopPropagation();
            handleRemoveEvent(event.id);
          }}
        >
          <IconX size={20} />
        </ActionIcon>
      )}

      <Box pr="lg">
        {!isDeleting && clientLabel && (
          <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6" mb="xxxs">
            {clientLabel}
          </Text>
        )}
        <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10" mr={isDeleting ? 80 : 'lg'}>
          {isDeleting
            ? `Meeting will be removed from Afternoon in (${secondsRemaining}s.)`
            : event.title}
        </Text>

        {!isDeleting && (
          <Box mt="xxxs">
            <EventTime
              scheduledStartAt={event.scheduledStartAt}
              scheduledEndAt={event.scheduledEndAt}
              platform={event.meetingPlatform}
            />
          </Box>
        )}
      </Box>

      {isDeleting ? (
        <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.4" mt="xs" mr={80}>
          This won’t cancel the meeting — it only disappears from your Upcoming meetings list in
          Afternoon
        </Text>
      ) : (
        <Box mt="sm" pt="xs" px="sm" mx="-sm" style={{ borderTop: '1px solid #f2f2f2' }}>
          {event.hasTranscription ? (
            <Tooltip label="Recording received. Details will be available once the meeting time has passed.">
              <Flex align="center" gap="xxxs" style={{ width: 'fit-content' }}>
                <IconMicrophone size={14} color="var(--mantine-color-brand-6)" />
                <Text size="xs" fw={FONT_WEIGHTS.semibold} c="brand.6">
                  Recording received
                </Text>
              </Flex>
            </Tooltip>
          ) : (
            <Text size="xs" fw={FONT_WEIGHTS.semibold} c="gray.6">
              Recording: {getFirstClientParticipant(event) ? 'Enabled' : 'Disabled'}
            </Text>
          )}
        </Box>
      )}
    </Paper>
  );
};
